/* eslint-disable @typescript-eslint/naming-convention */
import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { useFetch } from '@api/ApiHelper';
import { CollectionsListResponse } from '@api/v4/collections';
import { ListDropdown, ListOption } from '@components/library/dropdown';
import { FontIcon, FontIcons } from '@components/library/icon';
import { DefaultStatusLabels, ProgressStatus, Statuses } from '@components/library/status';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';

import classes from './styles/workspace-list.module.scss';

interface WorkspaceListProps {
  brandfolderKey?: string;
  brandfolderSlug?: string;
  showShareButton?: boolean;
}

export const WorkspaceList: FunctionComponent<WorkspaceListProps> = (props) => {
  const { brandfolderKey, brandfolderSlug, showShareButton } = props;
  const { brandfolder_key, brandfolder_slug } = BFG;
  const { pathname } = window.location;

  const { loading, response } = useFetch<CollectionsListResponse>({
    params: { is_workspace: true },
    url: `/api/v4/brandfolders/${brandfolderKey || brandfolder_key}/collections`
  });

  const [rendering, setRendering] = useState(true);
  const [options, setOptions] = useState<ListOption[]>([]);

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case Statuses.Completed:
        return bfTranslate(DefaultStatusLabels.Completed);
      case Statuses.InProgress:
        return bfTranslate(DefaultStatusLabels.InProgress);
      default:
        return bfTranslate(DefaultStatusLabels.NotStarted);
    }
  };

  const renderOptions = (): void => {
    setRendering(true);

    const listOptions: ListOption[] = response && response.data ? response.data.map((workspace) => {
      const { attributes } = workspace;
      const { name, progress, slug, status } = attributes;

      const href = `/${brandfolderSlug || brandfolder_slug}/${slug}`;
      const title = `${name}${pathname === href ? ` (${bfTranslate('Current')})` : ''}`;

      const label = (
        <a
          className={classes.link}
          href={href}
        >
          <FontIcon
            aria-hidden
            className={classes.icon}
            icon={FontIcons.Workspace}
          />
          <span
            className={classes.name}
            title={title}
          >
            {title}
          </span>
          {status && (
            <ProgressStatus
              className={classes['project-status']}
              label={getStatusLabel(status)}
              locale={determineUGTLocale()}
              progress={progress || 0}
              status={status as Statuses}
            />
          )}
        </a>
      );
      return {
        label,
        value: slug
      };
    }) : [];

    setOptions(listOptions);
    setRendering(false);
  };

  useEffect(() => {
    renderOptions();
  }, [response]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * This hack is needed because <a> tags inside of <ListDropdown> options don't work
   * with key events like hitting the enter key to activate the link
   * TODO: Refactor <ListDropdown> so that <a> tag options work onKeyUp/onKeyDown/onKeyPress
   */
  const handleOnChange = (listOption: ListOption): void => {
    window.location.href = `/${brandfolderSlug || brandfolder_slug}/${listOption.value}`;
  };

  const workspaces = response && response.data;

  const hasNoWorkspaces = workspaces && workspaces.length === 0;
  if (hasNoWorkspaces === true) return null;

  const hasOneWorkspace = workspaces && workspaces.length === 1;
  const workspaceText = hasOneWorkspace ? bfTranslate('Workspace') : bfTranslate('Workspaces');
  const placeholder = workspaces ? `${workspaces.length} ${workspaceText}` : '';

  return (
    <ListDropdown
      className={classnames(
        classes.dropdown,
        showShareButton && classes['show-share-button']
      )}
      isLoading={loading || rendering}
      onChange={(listOption): void => handleOnChange(listOption)}
      openOnClick
      options={options}
      placeholder={placeholder}
      virtualizeOptions
    />
  );
};
